import React, { useState, useEffect, useContext } from "react";
import api from "../../utils/Api";
import { AuthContext } from "../../context/AuthContext";
import { Plus, CalendarDays } from "lucide-react";

// ─── Utilidades ──────────────────────────────────────────────────────────────

/** Calcula días hábiles (lunes-viernes) entre dos strings YYYY-MM-DD, inclusive. */
function diasHabiles(inicio, fin) {
  if (!inicio || !fin || fin < inicio) return 0;
  let count = 0;
  const current = new Date(inicio + "T00:00:00");
  const end     = new Date(fin    + "T00:00:00");
  while (current <= end) {
    const dow = current.getDay(); // 0=dom, 6=sab
    if (dow !== 0 && dow !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

const estadoBadge = {
  aprobada:  "tn-badge tn-badge--green",
  pendiente: "tn-badge tn-badge--yellow",
  rechazada: "tn-badge tn-badge--red",
};

const tipoLabel = {
  vacaciones: "Vacaciones",
  enfermedad: "Enfermedad",
  permiso:    "Permiso",
};

// ─────────────────────────────────────────────────────────────────────────────

function AusenciasEmpleado() {
  const { token } = useContext(AuthContext);
  const [ausencias, setAusencias]   = useState([]);
  const [showModal, setShowModal]   = useState(false);
  const [error, setError]           = useState("");
  const [notification, setNotification] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    tipo: "vacaciones", fecha_inicio: "", fecha_fin: "", nota_trabajador: "",
  });

  // Días hábiles calculados en tiempo real desde el formulario
  const dias = diasHabiles(formData.fecha_inicio, formData.fecha_fin);

  useEffect(() => { if (token) loadAusencias(); }, [token]);

  const loadAusencias = async () => {
    try {
      const res = await api.get("/ausencias/mias", { headers: { Authorization: `Bearer ${token}` } });
      const raw = res.data;
      if (Array.isArray(raw))           setAusencias(raw);
      else if (Array.isArray(raw.data)) setAusencias(raw.data);
      else                              setAusencias([]);
    } catch {
      setError("Error al cargar las ausencias.");
      setAusencias([]);
    }
  };

  const handleChange = (e) => {
    setError("");
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const openModal = () => {
    setFormData({ tipo: "vacaciones", fecha_inicio: "", fecha_fin: "", nota_trabajador: "" });
    setError("");
    setShowModal(true);
  };

  const enviarSolicitud = async () => {
    setError("");
    if (!formData.fecha_inicio || !formData.fecha_fin) {
      setError("Las fechas de inicio y fin son obligatorias.");
      return;
    }
    if (formData.fecha_fin < formData.fecha_inicio) {
      setError("La fecha de fin no puede ser anterior a la de inicio.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/ausencias", formData, { headers: { Authorization: `Bearer ${token}` } });
      setNotification("Solicitud enviada correctamente.");
      setTimeout(() => setNotification(""), 4000);
      setShowModal(false);
      loadAusencias();
    } catch (err) {
      // Manejar error de solapamiento (422) o cualquier otro
      const msg = err.errors?.fecha_inicio?.[0]
        ?? err.response?.data?.message
        ?? err.message
        ?? "Error al enviar la solicitud.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 className="tn-page-title">Mis ausencias</h1>
          <p className="tn-page-sub">Solicita y consulta tus vacaciones, bajas y permisos</p>
        </div>
        <button className="tn-btn tn-btn--primary" onClick={openModal}>
          <Plus size={16} /> Solicitar ausencia
        </button>
      </div>

      {notification && <div className="tn-alert tn-alert--success" style={{ marginBottom: 16 }}>{notification}</div>}

      <div className="tn-card">
        {!Array.isArray(ausencias) || ausencias.length === 0 ? (
          <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--clr-muted)", fontSize: 14 }}>
            No tienes ausencias registradas
          </div>
        ) : (
          <div className="tn-table-wrap">
            <table className="tn-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Desde</th>
                  <th>Hasta</th>
                  <th>Días hábiles</th>
                  <th>Nota</th>
                  <th>Respuesta empresa</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {ausencias.map((a) => (
                  <tr key={a.id}>
                    <td data-label="Tipo" style={{ textTransform: "capitalize" }}>
                      {tipoLabel[a.tipo] ?? a.tipo}
                    </td>
                    <td data-label="Desde">
                      {new Date(a.fecha_inicio + "T00:00:00").toLocaleDateString("es-ES")}
                    </td>
                    <td data-label="Hasta">
                      {new Date(a.fecha_fin + "T00:00:00").toLocaleDateString("es-ES")}
                    </td>
                    <td data-label="Días hábiles">
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600, color: "var(--clr-accent)" }}>
                        <CalendarDays size={14} />
                        {a.dias_habiles ?? diasHabiles(a.fecha_inicio, a.fecha_fin)}
                      </span>
                    </td>
                    <td data-label="Nota">{a.nota_trabajador || "—"}</td>
                    <td data-label="Respuesta empresa">{a.nota_empresa || "—"}</td>
                    <td data-label="Estado">
                      <span className={estadoBadge[a.estado] ?? "tn-badge tn-badge--slate"}>
                        {a.estado}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de solicitud */}
      {showModal && (
        <div className="tn-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="tn-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="tn-modal__header">
              <span style={{ fontWeight: 700, fontSize: 15 }}>Nueva solicitud de ausencia</span>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--clr-muted)", fontSize: 20, lineHeight: 1 }}
              >×</button>
            </div>

            <div className="tn-modal__body">
              {error && <div className="tn-alert tn-alert--danger" style={{ marginBottom: 14 }}>{error}</div>}

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Tipo */}
                <div>
                  <label className="tn-label">Tipo de ausencia</label>
                  <select className="tn-input tn-select" id="tipo" value={formData.tipo} onChange={handleChange}>
                    <option value="vacaciones">Vacaciones</option>
                    <option value="enfermedad">Baja por enfermedad</option>
                    <option value="permiso">Permiso / asunto propio</option>
                  </select>
                </div>

                {/* Fechas */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label className="tn-label">Fecha de inicio</label>
                    <input type="date" className="tn-input" id="fecha_inicio"
                      value={formData.fecha_inicio} onChange={handleChange} />
                  </div>
                  <div>
                    <label className="tn-label">Fecha de fin</label>
                    <input type="date" className="tn-input" id="fecha_fin"
                      value={formData.fecha_fin} onChange={handleChange}
                      min={formData.fecha_inicio || undefined} />
                  </div>
                </div>

                {/* Contador días hábiles en tiempo real */}
                {dias > 0 && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                    background: "var(--clr-accent-bg)", borderRadius: 8,
                    border: "1px solid var(--clr-accent)", fontSize: 13,
                  }}>
                    <CalendarDays size={18} color="var(--clr-accent)" />
                    <span>
                      <strong style={{ color: "var(--clr-accent)" }}>{dias} día{dias !== 1 ? "s" : ""} hábil{dias !== 1 ? "es" : ""}</strong>
                      <span style={{ color: "var(--clr-muted)", marginLeft: 6 }}>
                        (lunes a viernes, fines de semana excluidos)
                      </span>
                    </span>
                  </div>
                )}

                {/* Nota opcional */}
                <div>
                  <label className="tn-label">Nota o motivo (opcional)</label>
                  <textarea className="tn-input tn-textarea" id="nota_trabajador" rows="3"
                    placeholder="Indica el motivo si lo deseas…"
                    value={formData.nota_trabajador} onChange={handleChange} />
                </div>
              </div>
            </div>

            <div className="tn-modal__footer">
              <button className="tn-btn tn-btn--ghost" onClick={() => setShowModal(false)}>Cancelar</button>
              <button
                className="tn-btn tn-btn--primary"
                onClick={enviarSolicitud}
                disabled={submitting || !formData.fecha_inicio || !formData.fecha_fin}
              >
                {submitting ? "Enviando…" : "Enviar solicitud"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AusenciasEmpleado;
